/**
 * Player-facing motion-blur preference. Stores a simple toggle + intensity
 * multiplier in localStorage so the choice persists across sessions. The
 * actual stylization (samples, falloff, chromatic split) lives in the
 * art-direction `StyleConfig.sfx.motion` block — this module only wraps the
 * runtime override.
 */
const STORAGE_KEY = 'tst:motion-blur-prefs:v1';

export interface MotionBlurDriveConfig {
  /** Master enable from StyleConfig (art-direction). */
  styleEnabled: boolean;
  /** Upper bound on the radial-blur shader strength. */
  radialStrength: number;
  /** Smoothstep lower bound on speed/maxSpeed (0..1). Above this, blur ramps in. */
  speedBandLow: number;
  /** Smoothstep upper bound on speed/maxSpeed (0..1). Saturates here. */
  speedBandHigh: number;
}

export interface MotionBlurDrivePrefs {
  /** Player toggle from Settings. */
  enabled: boolean;
  /** 0..1 multiplier on the configured strength. */
  intensity: number;
}

/**
 * Pure function — computes the final radial-blur strength to push to the
 * shader pass each frame. Single source of truth for the speed-band curve
 * so tests can pin the contract without booting Three.js.
 *
 *   target = radialStrength · smoothstep(speed01, low, high) · intensity
 *
 * Returns 0 when either toggle is off, the speed is below `speedBandLow`,
 * or radialStrength is zero. Saturates at `radialStrength × intensity` once
 * speed reaches `speedBandHigh`.
 */
export function computeMotionBlurStrength(
  speed: number,
  maxSpeed: number,
  config: MotionBlurDriveConfig,
  prefs: MotionBlurDrivePrefs,
): number {
  if (!config.styleEnabled || !prefs.enabled) return 0;
  if (config.radialStrength <= 0 || prefs.intensity <= 0) return 0;
  const safeMax = Math.max(1, maxSpeed);
  const speed01 = Math.max(0, speed) / safeMax;
  const lo = config.speedBandLow;
  const hi = Math.max(lo + 0.0001, config.speedBandHigh);
  const t = Math.max(0, Math.min(1, (speed01 - lo) / (hi - lo)));
  const eased = t * t * (3 - 2 * t); // smoothstep
  return config.radialStrength * eased * prefs.intensity;
}

export interface MotionBlurPrefsState {
  enabled: boolean;
  /** 0..1 multiplier on the configured radial strength. */
  intensity: number;
}

const DEFAULTS: MotionBlurPrefsState = {
  enabled: true,
  intensity: 0.85,
};

type Listener = (state: MotionBlurPrefsState) => void;

class MotionBlurPrefs {
  private state: MotionBlurPrefsState;
  private listeners = new Set<Listener>();
  /** Live debug readout — written by the render loop each frame, read by the dev panel. Public for lil-gui binding. */
  liveStrength = 0;

  constructor() {
    this.state = this.read();
  }

  isEnabled(): boolean { return this.state.enabled; }
  getIntensity(): number { return this.state.intensity; }

  setEnabled(enabled: boolean): void {
    if (this.state.enabled === enabled) return;
    this.state = { ...this.state, enabled };
    this.write();
    this.notify();
  }

  setIntensity(intensity: number): void {
    const clamped = Math.max(0, Math.min(1, intensity));
    if (Math.abs(this.state.intensity - clamped) < 0.0001) return;
    this.state = { ...this.state, intensity: clamped };
    this.write();
    this.notify();
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  private read(): MotionBlurPrefsState {
    if (typeof window === 'undefined') return { ...DEFAULTS };
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return { ...DEFAULTS };
      const parsed = JSON.parse(raw) as Partial<MotionBlurPrefsState>;
      return {
        enabled: typeof parsed.enabled === 'boolean' ? parsed.enabled : DEFAULTS.enabled,
        intensity: typeof parsed.intensity === 'number' && Number.isFinite(parsed.intensity)
          ? Math.max(0, Math.min(1, parsed.intensity))
          : DEFAULTS.intensity,
      };
    } catch {
      return { ...DEFAULTS };
    }
  }

  private write(): void {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch {
      /* ignore quota / private mode */
    }
  }

  private notify(): void {
    for (const listener of this.listeners) listener(this.state);
  }
}

export const motionBlurPrefs = new MotionBlurPrefs();
